
import React, { useState, useEffect, useMemo } from 'react';
import { UserProfile, PersonalityType, ViewState, Language, AIEngine } from './types.ts';
import PersonalitySelector from './components/PersonalitySelector.tsx';
import NameInput from './components/NameInput.tsx';
import CountrySelector from './components/CountrySelector.tsx';
import InstallPrompt from './components/InstallPrompt.tsx';
import Dashboard from './components/Dashboard.tsx';
import ChatInterface from './components/ChatInterface.tsx';
import ProfileView from './components/ProfileView.tsx';
import ModulesHub from './components/ModulesHub.tsx';
import LiveCompanion from './components/LiveCompanion.tsx';
import TimelineView from './components/TimelineView.tsx';
import CommunityHub from './components/CommunityHub.tsx';
import PredictionLab from './components/PredictionLab.tsx';
import DigitalBalanceHub from './components/DigitalBalanceHub.tsx';
import MicroLearningHub from './components/MicroLearningHub.tsx';
import StudioHub from './components/StudioHub.tsx';
import ImpactHub from './components/ImpactHub.tsx';
import LegacyHub from './components/LegacyHub.tsx';
import PhilosophyHub from './components/PhilosophyHub.tsx';
import CalmHub from './components/CalmHub.tsx';
import SocialHub from './components/SocialHub.tsx';
import CompanionHub from './components/CompanionHub.tsx';
import { TRANSLATIONS } from './translations.ts';
import { Home, MessageCircle, User as UserIcon, Clock, Grid, Wind, Menu, LogOut, MessageSquareHeart, Mic, Users, Sparkles, BookOpen, X } from 'lucide-react';

const App: React.FC = () => {
  const [view, setView] = useState<ViewState>(ViewState.ONBOARDING);
  const [onboardingStep, setOnboardingStep] = useState<'name' | 'country' | 'personality'>('name');
  const [tempName, setTempName] = useState('');
  const [tempCountry, setTempCountry] = useState('');
  const [tempLang, setTempLang] = useState<Language>(Language.KURDISH);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const t = useMemo(() => {
    const lang = user?.language || tempLang || Language.KURDISH;
    return TRANSLATIONS[lang] || TRANSLATIONS[Language.KURDISH] || {};
  }, [user?.language, tempLang]);

  const migrateUser = (savedData: any): UserProfile => {
    const lang = (savedData.language === 'ckb' || savedData.language === 'fa') 
      ? savedData.language 
      : Language.KURDISH;
      
    return {
      name: savedData.name || 'کاربر',
      language: lang,
      country: savedData.country || 'Kurdistan',
      selectedPersonality: savedData.selectedPersonality || PersonalityType.GARDENER,
      preferredEngine: savedData.preferredEngine || AIEngine.GEMINI,
      level: savedData.level || 1,
      xp: savedData.xp || 0,
      stats: {
        physical: savedData.stats?.physical ?? 60,
        mental: savedData.stats?.mental ?? 60,
        social: savedData.stats?.social ?? 60,
        professional: savedData.stats?.professional ?? 60,
        financial: savedData.stats?.financial ?? 60,
      },
      lifeProfile: {
        physical: { sleepHours: 7, activityLevel: 'moderate', dietaryPreference: 'معمولی', energyLevel: 7, healthIssues: [], ...savedData.lifeProfile?.physical },
        mental: { interests: [], learningGoals: [], stressLevel: 5, mood: 'خوب', ...savedData.lifeProfile?.mental },
        social: { socialBattery: 7, importantRelationsCount: 3, nextOccasion: '', lastSocialInteraction: Date.now(), ...savedData.lifeProfile?.social },
        professional: { skills: [], currentFocus: '', burnoutRisk: 2, careerGoal: '', ...savedData.lifeProfile?.professional },
        financial: { budgetStatus: 'balanced', financialStress: 3, savingsGoal: '', ...savedData.lifeProfile?.financial }
      },
      contentPreferences: { favorites: [], likedCategories: [], dislikedCategories: [], deliveryMode: 'smart', jokeFrequency: 'on-demand', ...savedData.contentPreferences },
      timeline: Array.isArray(savedData.timeline) ? savedData.timeline : [],
      diaryEntries: Array.isArray(savedData.diaryEntries) ? savedData.diaryEntries : [],
      chatHistory: Array.isArray(savedData.chatHistory) ? savedData.chatHistory : [],
      communityProfile: { anonymousName: 'کاربر-' + Math.floor(Math.random() * 1000), anonymousColor: 'bg-blue-500', trustScore: 0, joinedRooms: [], matches: [], ...savedData.communityProfile },
      appUsage: { dailyMinutes: 0, lastResetDate: new Date().toISOString().split('T')[0], ...savedData.appUsage },
      microLearning: { activeChallenge: null, completedClasses: [], badges: [], lastClassDate: '', ...savedData.microLearning },
      immersiveProfile: { voice: { id: 'fa-1', speed: 1, pitch: 1 }, music: { mood: 'relax', volume: 0.5, isPlaying: false, currentTrack: '' }, avatarId: '1', ...savedData.immersiveProfile },
      feedbackSystem: { pendingSurveyQuestion: null, history: [], reports: [], lastSurveyDate: '', ...savedData.feedbackSystem },
      legacyProfile: { wisdom: null, postMortemAction: 'archive', executor: null, dataRetentionPeriod: 365, ...savedData.legacyProfile },
      philosophyProfile: { coreValues: [], answeredQuestions: [], gratitudeJournal: [], activePractice: null, ...savedData.philosophyProfile },
      anxietyProfile: { history: [], triggers: [], fearLadder: [], cbtHistory: [], lastCheckIn: 0, safetyPlan: { warningSigns: [], copingStrategies: [], socialSupports: [], professionalSupports: [] }, ...savedData.anxietyProfile },
      socialTrainingProfile: { level: 'beginner', isAssessmentCompleted: false, completedLessons: [], roleplayHistory: [], weeklyGoal: '', confidenceScore: 50, logs: [], monthlyReports: [], skills: [], badges: [], weeklyPlan: [], ...savedData.socialTrainingProfile },
      traits: Array.isArray(savedData.traits) ? savedData.traits : []
    };
  };

  useEffect(() => {
    const savedUser = localStorage.getItem('hawre_user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(migrateUser(parsed));
        setView(ViewState.DASHBOARD);
      } catch (e) {
        localStorage.removeItem('hawre_user');
      }
    }
  }, []);

  const handleUpdateUser = (updatedUser: UserProfile) => {
    setUser(updatedUser);
    localStorage.setItem('hawre_user', JSON.stringify(updatedUser));
  };

  const confirmPersonality = () => {
    if (user) {
      handleUpdateUser(user);
      setView(ViewState.DASHBOARD);
    }
  };

  const NAV_ITEMS = useMemo(() => [
    { id: ViewState.DASHBOARD, label: t.dashboard, icon: Home, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: ViewState.COMPANION_LIVE, label: t.companion_live, icon: Mic, color: 'text-rose-600', bg: 'bg-rose-50' },
    { id: ViewState.CHAT, label: t.chat, icon: MessageCircle, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { id: ViewState.COMPANION, label: t.companion, icon: BookOpen, color: 'text-amber-600', bg: 'bg-amber-50' },
    { id: ViewState.CALM, label: t.calm, icon: Wind, color: 'text-teal-600', bg: 'bg-teal-50' },
    { id: ViewState.SOCIAL_TRAINING, label: t.social_training, icon: MessageSquareHeart, color: 'text-orange-600', bg: 'bg-orange-50' },
    { id: ViewState.COMMUNITY, label: t.community, icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
    { id: ViewState.MODULES, label: t.modules, icon: Grid, color: 'text-slate-600', bg: 'bg-slate-100' },
    { id: ViewState.TIMELINE, label: t.timeline, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { id: ViewState.PROFILE, label: t.profile, icon: UserIcon, color: 'text-slate-700', bg: 'bg-slate-100' },
  ], [t]);

  if (view === ViewState.ONBOARDING) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-lg bg-white rounded-[3rem] shadow-2xl border border-slate-100 overflow-hidden animate-scale">
          {onboardingStep === 'name' ? (
            <NameInput 
              t={t}
              currentLang={tempLang}
              onLangChange={setTempLang}
              onNext={(n, l) => { setTempName(n); setTempLang(l); setOnboardingStep('country'); }} 
            />
          ) : onboardingStep === 'country' ? (
            <CountrySelector 
              t={t}
              selected={tempCountry}
              onSelect={setTempCountry}
              onConfirm={() => setOnboardingStep('personality')}
            />
          ) : (
            <PersonalitySelector 
              t={t}
              selected={user?.selectedPersonality || null} 
              onSelect={(p) => setUser(migrateUser({ name: tempName, language: tempLang, country: tempCountry, selectedPersonality: p }))} 
              onConfirm={confirmPersonality} 
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 flex flex-col md:flex-row overflow-hidden font-sans" dir="rtl">
      <InstallPrompt t={t} />
      
      {/* Sidebar Desktop */}
      <aside className="hidden md:flex flex-col w-72 bg-white border-l border-slate-200 p-8 overflow-y-auto shrink-0 shadow-lg z-20">
        <div className="flex items-center gap-3 mb-12">
            <div className="p-2.5 bg-indigo-600 rounded-2xl text-white shadow-lg shadow-indigo-100 rotate-3">
                <Sparkles className="w-7 h-7" />
            </div>
            <h1 className="text-2xl font-black text-slate-800 tracking-tight">{t.appName}</h1>
        </div>
        <nav className="space-y-2 flex-1">
          {NAV_ITEMS.map((item) => (
            <button 
              key={item.id} 
              onClick={() => setView(item.id)} 
              className={`w-full flex items-center gap-4 p-4 rounded-2xl font-black transition-all duration-300 group
                ${view === item.id 
                  ? `${item.bg} ${item.color} shadow-sm translate-x-[-4px]` 
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                }`}
            >
              <item.icon className={`w-5 h-5 transition-transform group-hover:scale-110`} />
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="mt-8 pt-8 border-t border-slate-100">
          <button 
            onClick={() => { localStorage.removeItem('hawre_user'); window.location.reload(); }} 
            className="w-full flex items-center gap-3 text-red-500 text-sm p-4 rounded-2xl hover:bg-red-50 font-black transition-colors"
          >
            <LogOut className="w-5 h-5" /> {t.logout}
          </button>
        </div>
      </aside>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setIsMobileMenuOpen(false)}></div>
          <div className="relative w-80 h-full bg-white p-8 shadow-2xl overflow-y-auto flex flex-col animate-fade-in-right">
             <div className="flex items-center justify-between mb-12">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-600 rounded-xl text-white shadow-lg"><Sparkles className="w-6 h-6" /></div>
                  <h1 className="text-2xl font-black text-slate-800 tracking-tight">{t.appName}</h1>
                </div>
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 bg-slate-100 rounded-full text-slate-500"><X className="w-6 h-6" /></button>
             </div>
             <nav className="space-y-2 flex-1">
                {NAV_ITEMS.map((item) => (
                  <button 
                    key={item.id} 
                    onClick={() => { setView(item.id); setIsMobileMenuOpen(false); }} 
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl font-black transition-all
                      ${view === item.id ? `${item.bg} ${item.color} shadow-sm` : 'text-slate-500'}
                    `}
                  >
                    <item.icon className="w-6 h-6" /> 
                    <span className="text-base">{item.label}</span>
                  </button>
                ))}
             </nav>
             <button 
                onClick={() => { localStorage.removeItem('hawre_user'); window.location.reload(); }} 
                className="mt-auto flex items-center gap-3 text-red-500 text-sm p-4 rounded-2xl hover:bg-red-50 font-black"
              >
                <LogOut className="w-5 h-5" /> {t.logout}
              </button>
          </div>
        </div>
      )}

      {/* Main Area */}
      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <header className="md:hidden glass border-b p-5 flex justify-between items-center sticky top-0 z-40">
           <div className="flex items-center gap-2">
              <div className="p-1.5 bg-indigo-600 rounded-lg text-white"><Sparkles className="w-4 h-4" /></div>
              <span className="font-black text-slate-800 tracking-tight">{t.appName}</span>
           </div>
           <button onClick={() => setIsMobileMenuOpen(true)} className="p-2.5 bg-slate-100 rounded-xl text-slate-600 transition-colors active:scale-95"><Menu className="w-6 h-6" /></button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-12 pb-24 md:pb-12 scroll-smooth no-scrollbar">
          <div className="max-w-6xl mx-auto w-full">
            {user && (
              <div className="animate-fade-in">
                {view === ViewState.DASHBOARD && <Dashboard user={user} onNavigate={setView} />}
                {view === ViewState.CHAT && <ChatInterface user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.PROFILE && <ProfileView user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.COMPANION_LIVE && <LiveCompanion user={user} />}
                {view === ViewState.SOCIAL_TRAINING && <SocialHub user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.CALM && <CalmHub user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.MODULES && <ModulesHub user={user} onNavigate={setView} />}
                {view === ViewState.TIMELINE && <TimelineView user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.COMMUNITY && <CommunityHub user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.COMPANION && <CompanionHub user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.PREDICTION && <PredictionLab user={user} />}
                {view === ViewState.BALANCE && <DigitalBalanceHub user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.LEARNING && <MicroLearningHub user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.STUDIO && <StudioHub user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.IMPACT && <ImpactHub user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.LEGACY && <LegacyHub user={user} onUpdateUser={handleUpdateUser} />}
                {view === ViewState.PHILOSOPHY && <PhilosophyHub user={user} onUpdateUser={handleUpdateUser} />}
              </div>
            )}
          </div>
        </div>
        
        {/* Navigation Indicator (Mobile Only) */}
        <div className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 glass border border-slate-200/50 px-6 py-3 rounded-full shadow-2xl z-50 flex items-center gap-6">
           {NAV_ITEMS.slice(0, 5).map((item) => (
             <button 
              key={item.id} 
              onClick={() => setView(item.id)} 
              className={`p-2 transition-all duration-300 ${view === item.id ? `${item.color} scale-125` : 'text-slate-400 opacity-60'}`}
             >
               <item.icon className="w-6 h-6" />
             </button>
           ))}
        </div>
      </main>
    </div>
  );
};

export default App;
